/**
 * Read-adapter from the real `payroll.listEssInvites` wire shape to the UI's
 * `EssInvite` type (#23314). Pure, no React — mirrors
 * `wagepro_store_leave.ts`'s `deriveLeaveRequestsFromRecords` pattern.
 *
 * The wire row is a straight projection of `employee_ess_invites` (the
 * projection the router selects at payroll.ts:10458-10465) minus the raw
 * `invite_token` (that field is one-time, shown only in the
 * `createEssInvite` response and never re-queryable — see the doc comment
 * on `listEssInvites` for why).
 */
import type { EssEmployeeLookup } from './types_ess_invites';

/**
 * The three invite statuses the backend persists on `employee_ess_invites`
 * (see the `.status` update sites in payroll.ts §createEssInvite/§revokeEssInvite
 * and personal_payroll.ts §acceptEssInvite). Any other wire value is normalised
 * to `pending` — same stance `wagepro_store_leave.ts` documents for unknown
 * statuses.
 */
export type EssInviteStatus = 'pending' | 'accepted' | 'revoked';

/**
 * The wire fields this adapter reads. Intentionally a subset of the Drizzle
 * row — new server-only columns are safe to add without shape drift here.
 */
export interface RealEssInviteRecord {
  id: string;
  employee_id: string;
  /** Raw wire string; `pending`/`accepted`/`revoked` in practice. */
  status: string;
  invited_email: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
}

/** UI-facing invite (all display-derived fields resolved). */
export interface EssInvite {
  id: string;
  employee_id: string;
  /** Resolved from `payroll.listEmployees`; `null` when the employee row is missing. */
  employee_name: string | null;
  status: EssInviteStatus;
  invited_email: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
}

const ESS_INVITE_STATUSES: readonly EssInviteStatus[] = [
  'pending',
  'accepted',
  'revoked',
];

function normaliseStatus(raw: string): EssInviteStatus {
  return (ESS_INVITE_STATUSES as readonly string[]).includes(raw)
    ? (raw as EssInviteStatus)
    : 'pending';
}

/**
 * Adapts real wire rows into the UI shape. Pure and DI-testable — the
 * separate `records` and `employees` args mirror `deriveLeaveRequestsFromRecords`.
 */
export function deriveEssInvitesFromRecords(
  records: readonly RealEssInviteRecord[],
  employees: readonly EssEmployeeLookup[],
): EssInvite[] {
  const name_by_id = new Map(
    employees.map((emp) => [emp.id, emp.full_name] as const),
  );
  return records.map((record) => ({
    id: record.id,
    employee_id: record.employee_id,
    employee_name: name_by_id.get(record.employee_id) ?? null,
    status: normaliseStatus(record.status),
    invited_email: record.invited_email,
    created_at: record.created_at,
    expires_at: record.expires_at,
    accepted_at: record.accepted_at,
  }));
}

/**
 * The employer-facing summary rendered next to an employee's name — one of
 * six discrete states. `not_invited` is the terminal "no active invite, no
 * accepted link" state; `no_email` is the same but with no address on file
 * to send an invite to; `pending` shows a live consumable invite; `expired`
 * shows the most recent invite is still `pending` on the row but past its
 * `expires_at`, so it can no longer be accepted; `linked` shows an accepted,
 * still-active link; `revoked` shows the most recent invite was revoked
 * without acceptance (the tell that the employer chose to withdraw the offer
 * before consent).
 */
export type EssEmployeeStatus =
  | 'not_invited'
  | 'no_email'
  | 'pending'
  | 'expired'
  | 'linked'
  | 'revoked';

/**
 * The one invite action the employer can take from a given status. `invite`
 * for a first-ever invite, `resend` for a live invite (replaces it — the
 * server revokes the earlier pending row), `reinvite` once the last invite
 * is dead (expired or revoked). `null` when there is nothing to send:
 * already linked, or no email to send it to.
 */
export type EssInviteAction = 'invite' | 'resend' | 'reinvite';

export function essInviteActionFor(
  status: EssEmployeeStatus,
): EssInviteAction | null {
  switch (status) {
    case 'not_invited':
      return 'invite';
    case 'pending':
      return 'resend';
    case 'expired':
    case 'revoked':
      return 'reinvite';
    case 'linked':
    case 'no_email':
      return null;
  }
}

/**
 * A `pending` row is only consumable until `expires_at`. Compared as parsed
 * instants, not strings: `…:00Z` and `…:00.000Z` are the same moment but do
 * not sort lexically. An unparseable timestamp is treated as not expired —
 * the server's accept path stays the authority on expiry.
 */
export function isEssInviteExpired(
  invite: EssInvite,
  now_iso: string,
): boolean {
  if (invite.status !== 'pending') return false;
  const expires = Date.parse(invite.expires_at);
  const now = Date.parse(now_iso);
  if (Number.isNaN(expires) || Number.isNaN(now)) return false;
  return expires <= now;
}

/**
 * Selects the most-recently-created invite for one employee. `listEssInvites`
 * returns rows entity-wide, so the profile screen needs a per-employee slice
 * to make status decisions. `undefined` means the employee has never been
 * invited.
 */
export function mostRecentInviteForEmployee(
  invites: readonly EssInvite[],
  employee_id: string,
): EssInvite | undefined {
  let best: EssInvite | undefined;
  for (const invite of invites) {
    if (invite.employee_id !== employee_id) continue;
    if (!best || invite.created_at > best.created_at) best = invite;
  }
  return best;
}

/**
 * Employee-level status projection over the invites list.
 *
 * `employeeUserLinked` (from `employees.employee_user_id`) is the authoritative
 * signal of "does this employee have live self-service access RIGHT NOW":
 *
 *   - `true` — return `linked` unconditionally. The ESS grant survives the
 *     invite being marked `revoked` on the row, so a `revoked` invite plus a
 *     still-linked employee_user is `linked`, not `revoked` (see
 *     revokeEssInvite semantics in personal_payroll.test.ts §"is a no-op on
 *     the granted link when the invite was already ACCEPTED").
 *   - `false` — access is NOT live. Even if the most-recent invite row still
 *     carries `status='accepted'`, the access was severed by revokeEssAccess
 *     (which clears `employee_user_id` but leaves the invite row's status
 *     untouched, `payroll.ts` §revokeEssAccessForEmployee). Report `revoked`
 *     so the employer can re-invite — treating it as `linked` would falsely
 *     imply live access and block the "Send new invite" affordance (Finding
 *     #1 in the #23314 adversarial review).
 */
export function deriveEssStatusForEmployee(input: {
  invites: readonly EssInvite[];
  employee_id: string;
  employeeUserLinked: boolean;
  /**
   * `false` only when the directory row is known to carry no email. Defaults
   * to `true` (unknown) so the server's own `no_email` BAD_REQUEST stays the
   * authority wherever the caller can't see the address.
   */
  employeeHasEmail?: boolean;
  /** Clock for the expiry check; defaults to the current time. */
  now_iso?: string;
}): EssEmployeeStatus {
  if (input.employeeUserLinked) return 'linked';
  const invite = mostRecentInviteForEmployee(input.invites, input.employee_id);
  const now_iso = input.now_iso ?? new Date().toISOString();
  // A live invite was already sent to the bound address, so it stays
  // `pending` even if the email has since been removed from the employee.
  if (invite?.status === 'pending' && !isEssInviteExpired(invite, now_iso)) {
    return 'pending';
  }
  if (input.employeeHasEmail === false) return 'no_email';
  if (!invite) return 'not_invited';
  if (invite.status === 'pending') return 'expired';
  // Both `accepted` (with employeeUserLinked=false = post-revokeEssAccess) and
  // `revoked` (invite killed before consumption) collapse to `revoked` here:
  // access is not live, and the employer's next action is the same in both
  // cases — mint a fresh invite. Distinguishing them in the badge would be
  // a UX noise-word for zero product value.
  return 'revoked';
}
